import React, { useState } from 'react';
import moment from 'moment-timezone';
import { Calendar as CalendarIcon } from 'lucide-react';
import { Calendar } from 'App/shadcn/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from 'App/shadcn/ui/popover';
import { Button } from 'App/shadcn/ui/button';
import { cn } from 'App/shadcn/lib/utils';

type Props = {
	value: string; // ISO-ish: "YYYY-MM-DDTHH:mm[:ss]" or "YYYY-MM-DD HH:mm"
	onChange: (iso: string) => void;
	disabled?: boolean;
	className?: string;
	/**
	 * When true, the trigger reads as plain text with no chrome until hover/focus.
	 * Good for inline summary cards where the form affordance should be implicit.
	 */
	ghost?: boolean;
};

/** The user's CRM timezone (set globally as `window.userTimezone`), the same
 *  source the rest of the app formats dates against. moment's *default* tz is
 *  the browser's guessed zone (AppActions sets `moment.tz.setDefault(moment.tz.guess())`),
 *  so we must pass this explicitly — otherwise the picker shows the time in the
 *  browser zone, which mismatches the opened entity. */
function userTimezone(): string {
	const tz = (typeof window !== 'undefined' && (window as { userTimezone?: string }).userTimezone) || '';
	return tz && moment.tz.zone(tz) ? tz : 'Europe/Stockholm';
}

/**
 * Show the datetime in the user's CRM timezone — matching how the opened
 * appointment/activity and the rest of the app render it. `moment.tz(value, tz)`
 * honours any offset embedded in `value` (e.g. `...-04:00`) and converts to the
 * user's zone, so a naive value is read as user-local and an offset-bearing one
 * is converted correctly.
 */
function parse(value: string): { date: Date | undefined; time: string } {
	if (!value) return { date: undefined, time: '09:00' };
	const m = moment.tz(value.replace(' ', 'T'), userTimezone());
	if (!m.isValid()) return { date: undefined, time: '09:00' };
	// Browser-local Date carrying the user-tz wall-clock Y/M/D so the calendar
	// highlights the right day without a second shift.
	return { date: new Date(m.year(), m.month(), m.date()), time: m.format('HH:mm') };
}

/**
 * Interpret the picked wall-clock in the user's timezone and emit an ISO string
 * WITH that offset (e.g. `2026-05-27T10:00:00-04:00`), so the saved time
 * round-trips back to the same wall-clock the user picked.
 */
function toIso(date: Date, time: string): string {
	const pad = (n: number) => String(n).padStart(2, '0');
	const wall = `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${time}`;
	return moment.tz(wall, userTimezone()).format();
}

function formatDisplay(date: Date | undefined, time: string): string {
	if (!date) return 'Pick a date';
	const pad = (n: number) => String(n).padStart(2, '0');
	return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${time}`;
}

const DateTimePicker = ({ value, onChange, disabled, className, ghost }: Props) => {
	const parsed = parse(value);
	const [open, setOpen] = useState(false);
	const [time, setTime] = useState(parsed.time);

	return (
		<Popover open={open} onOpenChange={setOpen}>
			<PopoverTrigger asChild>
				<Button
					type="button"
					variant={ghost ? 'ghost' : 'outline'}
					disabled={disabled}
					className={cn(
						'h-8 justify-start gap-2 text-body-sm font-normal text-foreground',
						ghost &&
							'border border-transparent bg-transparent px-2 hover:border-input hover:bg-background focus-visible:border-input data-[state=open]:border-input data-[state=open]:bg-background [&_svg]:opacity-0 hover:[&_svg]:opacity-100 focus-visible:[&_svg]:opacity-100 data-[state=open]:[&_svg]:opacity-100',
						// In ghost mode with a value, drop the gap to the (hidden)
						// calendar icon so the date text aligns with sibling fields.
						ghost && parsed.date && 'gap-0',
						!parsed.date && 'text-muted-foreground',
						className
					)}
				>
					{(!ghost || !parsed.date) && (
						<CalendarIcon className="h-3.5 w-3.5 text-muted-foreground transition-opacity" />
					)}
					{formatDisplay(parsed.date, parsed.time)}
				</Button>
			</PopoverTrigger>
			<PopoverContent align="start" className="w-auto p-0">
				<Calendar
					mode="single"
					selected={parsed.date}
					onSelect={d => {
						if (d) onChange(toIso(d, time));
					}}
					initialFocus
				/>
				<div className="flex items-center gap-2 border-t px-3 py-2">
					<span className="text-body-sm text-muted-foreground">{'Time'}</span>
					<input
						type="time"
						value={time}
						onChange={e => {
							setTime(e.target.value);
							if (parsed.date) onChange(toIso(parsed.date, e.target.value));
						}}
						className="h-7 rounded-md border border-input bg-transparent px-2 text-body-sm outline-none focus:border-ring"
					/>
					<Button size="sm" variant="ghost" className="ml-auto h-7" onClick={() => setOpen(false)}>
						{'Done'}
					</Button>
				</div>
			</PopoverContent>
		</Popover>
	);
};

export default DateTimePicker;
